import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Calendar as BigCalendar, dateFnsLocalizer, Views } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { ro } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { api, EVENT_TYPE_MAP, EVENT_TYPES } from "@/lib/api";
import EventDialog from "@/components/EventDialog";
import { Calendar as CalendarIcon, Plus, Filter, ChevronLeft, ChevronRight } from "lucide-react";

const locales = { ro };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});

const messagesRo = {
  date: "Data",
  time: "Ora",
  event: "Eveniment",
  allDay: "Toată ziua",
  week: "Săptămână",
  work_week: "Săpt. lucrătoare",
  day: "Zi",
  month: "Lună",
  previous: "Înapoi",
  next: "Înainte",
  yesterday: "Ieri",
  tomorrow: "Mâine",
  today: "Azi",
  agenda: "Agendă",
  noEventsInRange: "Niciun eveniment în această perioadă.",
  showMore: (n) => `+ încă ${n}`,
};

function CustomToolbar({ label, onNavigate, onView, view }) {
  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 mb-3">
      <div className="flex items-center gap-2">
        <button
          data-testid="cal-today"
          onClick={() => onNavigate("TODAY")}
          className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-xs font-medium"
        >
          Azi
        </button>
        <div className="flex items-center bg-white border border-slate-200 rounded-full">
          <button data-testid="cal-prev" onClick={() => onNavigate("PREV")} className="p-1.5 rounded-l-full hover:bg-slate-50">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button data-testid="cal-next" onClick={() => onNavigate("NEXT")} className="p-1.5 rounded-r-full hover:bg-slate-50">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="font-display text-base sm:text-lg font-medium text-slate-900 capitalize">{label}</div>
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1 self-end sm:self-auto">
        {[
          { v: Views.MONTH, label: "Lună" },
          { v: Views.WEEK,  label: "Săptămână" },
          { v: Views.DAY,   label: "Zi" },
        ].map((b) => (
          <button
            key={b.v}
            data-testid={`cal-view-${b.v}`}
            onClick={() => onView(b.v)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              view === b.v ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {b.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [view, setView] = useState(Views.MONTH);
  const [date, setDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [filters, setFilters] = useState({ types: new Set(), showCancelled: false });
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar/events");
      setEvents(data || []);
    } catch (err) {
      // Non-blocking: keep current events visible; log for debugging
      console.error("[Calendar] failed to load events:", err);
    }
  }, []);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("data:refresh", h);
    return () => window.removeEventListener("data:refresh", h);
  }, [load]);

  // Auto-open create dialog when arriving with ?new=1
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setEditing(null);
      setDefaults({});
      setDialogOpen(true);
      // remove the param so reload doesn't re-trigger
      const p = new URLSearchParams(searchParams);
      p.delete("new");
      setSearchParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calendarEvents = useMemo(() => {
    return events
      .filter((e) => filters.showCancelled || e.status !== "cancelled")
      .filter((e) => filters.types.size === 0 || filters.types.has(e.event_type))
      .map((e) => ({
        id: e.id,
        title: e.title,
        start: new Date(e.start_datetime),
        end: e.end_datetime ? new Date(e.end_datetime) : new Date(new Date(e.start_datetime).getTime() + 30 * 60000),
        allDay: !!e.all_day,
        resource: e,
      }));
  }, [events, filters]);

  const eventStyleGetter = (event) => {
    const ev = event.resource;
    const t = EVENT_TYPE_MAP[ev?.event_type] || EVENT_TYPE_MAP.custom;
    const color = ev?.color || t.color;
    const isDone = ev?.status === "done";
    const isCancelled = ev?.status === "cancelled";
    return {
      style: {
        backgroundColor: isCancelled ? "#94A3B8" : color,
        opacity: isDone ? 0.55 : isCancelled ? 0.6 : 1,
        textDecoration: isDone || isCancelled ? "line-through" : "none",
        border: "none",
        color: "#fff",
        borderRadius: "6px",
        fontSize: "12px",
        padding: "2px 6px",
        fontWeight: 500,
      },
    };
  };

  function openCreate(start, end) {
    setEditing(null);
    setDefaults({
      defaultStart: start ? toLocalInput(start) : undefined,
      defaultEnd: end ? toLocalInput(end) : undefined,
    });
    setDialogOpen(true);
  }

  function openEdit(ev) {
    setEditing(ev.resource);
    setDefaults({});
    setDialogOpen(true);
  }

  function toggleType(t) {
    setFilters((f) => {
      const types = new Set(f.types);
      if (types.has(t)) types.delete(t); else types.add(t);
      return { ...f, types };
    });
  }

  return (
    <div className="sm:pl-52 space-y-4" data-testid="calendar-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Activități & follow-up</div>
          <h1 className="font-display text-3xl tracking-tight font-medium flex items-center gap-2">
            <CalendarIcon className="w-7 h-7 text-[#0047FF]" />
            Calendar
          </h1>
        </div>
        <button
          data-testid="calendar-add-event"
          onClick={() => openCreate()}
          className="bg-[#0047FF] hover:bg-[#003BD3] text-white text-sm px-4 py-2 rounded-full inline-flex items-center gap-1.5 shadow-sm"
        >
          <Plus className="w-4 h-4" /> Eveniment nou
        </button>
      </div>

      {/* Type filter chips */}
      <div className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest text-slate-400 inline-flex items-center gap-1">
          <Filter className="w-3 h-3" /> Filtre
        </span>
        {EVENT_TYPES.map((t) => {
          const active = filters.types.has(t.value);
          return (
            <button
              key={t.value}
              data-testid={`cal-filter-${t.value}`}
              onClick={() => toggleType(t.value)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-all inline-flex items-center gap-1 ${
                active ? "border-transparent text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
              style={active ? { backgroundColor: t.color } : {}}
            >
              <span>{t.emoji}</span> {t.label}
            </button>
          );
        })}
        <label className="ml-auto inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.showCancelled}
            onChange={(e) => setFilters((f) => ({ ...f, showCancelled: e.target.checked }))}
            className="rounded border-slate-300"
          />
          Arată anulate
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-4">
        <BigCalendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          views={[Views.MONTH, Views.WEEK, Views.DAY]}
          view={view}
          date={date}
          onView={setView}
          onNavigate={setDate}
          onSelectEvent={openEdit}
          onSelectSlot={({ start, end }) => openCreate(start, end)}
          selectable
          popup
          step={30}
          timeslots={2}
          messages={messagesRo}
          culture="ro"
          eventPropGetter={eventStyleGetter}
          formats={{
            timeGutterFormat: (d, culture, l) => l.format(d, "HH:mm", culture),
            eventTimeRangeFormat: ({ start, end }, culture, l) =>
              `${l.format(start, "HH:mm", culture)} – ${l.format(end, "HH:mm", culture)}`,
            agendaTimeFormat: (d, culture, l) => l.format(d, "HH:mm", culture),
            agendaTimeRangeFormat: ({ start, end }, culture, l) =>
              `${l.format(start, "HH:mm", culture)} – ${l.format(end, "HH:mm", culture)}`,
            agendaDateFormat: (d, culture, l) => l.format(d, "EEEE, dd/MM/yyyy", culture),
            dayHeaderFormat: (d, culture, l) => l.format(d, "EEEE, dd/MM/yyyy", culture),
            dayRangeHeaderFormat: ({ start, end }, culture, l) =>
              `${l.format(start, "dd/MM", culture)} – ${l.format(end, "dd/MM/yyyy", culture)}`,
            monthHeaderFormat: (d, culture, l) => l.format(d, "MMMM yyyy", culture),
          }}
          components={{ toolbar: CustomToolbar }}
          style={{ height: "calc(100vh - 280px)", minHeight: 520 }}
        />
      </div>

      <EventDialog
        open={dialogOpen}
        event={editing}
        defaultStart={defaults.defaultStart}
        defaultEnd={defaults.defaultEnd}
        onClose={(refresh) => {
          setDialogOpen(false);
          setEditing(null);
          setDefaults({});
          if (refresh) load();
        }}
      />
    </div>
  );
}

function toLocalInput(d) {
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}
